import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  DollarSign, FileText, AlertTriangle, TrendingUp, RefreshCw,
  Send, Eye, XCircle, CheckCircle, Clock, Search, Filter,
  Download, BarChart3, PieChart, ArrowUpRight, ArrowDownRight,
  CreditCard, Building2, Users, Calendar, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BillingRCM() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  
  // Claims state
  const [claims, setClaims] = useState([]);
  const [claimsSummary, setClaimsSummary] = useState({});
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimDetailOpen, setClaimDetailOpen] = useState(false);
  const [claimStatusFilter, setClaimStatusFilter] = useState('');
  
  // Payments state
  const [payments, setPayments] = useState([]);
  const [paymentsSummary, setPaymentsSummary] = useState({});
  
  // Denials state
  const [denials, setDenials] = useState([]);
  const [denialsSummary, setDenialsSummary] = useState({});
  const [selectedDenial, setSelectedDenial] = useState(null);
  const [denialDetailOpen, setDenialDetailOpen] = useState(false);
  
  // Analytics state
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [agingReport, setAgingReport] = useState(null);
  const [denialTrends, setDenialTrends] = useState(null);
  
  // Reference data
  const [payers, setPayers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'claims') fetchClaims();
    if (activeTab === 'payments') fetchPayments();
    if (activeTab === 'denials') fetchDenials();
    if (activeTab === 'analytics') fetchAnalytics();
  }, [activeTab, claimStatusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, payersRes, servicesRes] = await Promise.all([
        fetch(`${API_URL}/api/phase56/dashboard`),
        fetch(`${API_URL}/api/phase56/payers`),
        fetch(`${API_URL}/api/phase56/service-types`)
      ]);

      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboard(data);
      }

      if (payersRes.ok) {
        const data = await payersRes.json();
        setPayers(data.payers || []);
      }

      if (servicesRes.ok) {
        const data = await servicesRes.json();
        setServiceTypes(data.service_types || []);
      }

      await fetchClaims();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClaims = async () => {
    try {
      const params = new URLSearchParams();
      if (claimStatusFilter) params.append('status', claimStatusFilter);
      
      const response = await fetch(`${API_URL}/api/phase56/claims?${params}`);
      if (response.ok) {
        const data = await response.json();
        setClaims(data.claims || []);
        setClaimsSummary(data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase56/payments`);
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
        setPaymentsSummary(data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchDenials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase56/denials`);
      if (response.ok) {
        const data = await response.json();
        setDenials(data.denials || []);
        setDenialsSummary(data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching denials:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const [revenueRes, agingRes, trendsRes] = await Promise.all([
        fetch(`${API_URL}/api/phase56/analytics/revenue?period=month`),
        fetch(`${API_URL}/api/phase56/analytics/aging`),
        fetch(`${API_URL}/api/phase56/analytics/denial-trends`)
      ]);

      if (revenueRes.ok) setRevenueAnalytics(await revenueRes.json());
      if (agingRes.ok) setAgingReport(await agingRes.json());
      if (trendsRes.ok) setDenialTrends(await trendsRes.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleViewClaim = async (claimId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase56/claims/${claimId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedClaim(data);
        setClaimDetailOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load claim details');
    }
  };

  const handleSubmitClaim = async (claimId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase56/claims/${claimId}/submit`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Claim submitted successfully');
        fetchClaims();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit claim');
      }
    } catch (error) {
      toast.error('Failed to submit claim');
    }
  };

  const handleViewDenial = async (denialId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase56/denials/${denialId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedDenial(data);
        setDenialDetailOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load denial details');
    }
  };

  const handleAppealDenial = async (denialId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase56/denials/${denialId}/appeal?appeal_reason=Medical%20necessity%20documentation%20provided`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Appeal filed successfully');
        fetchDenials();
        setDenialDetailOpen(false);
      }
    } catch (error) {
      toast.error('Failed to file appeal');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      submitted: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      denied: 'bg-red-100 text-red-700',
      paid: 'bg-emerald-100 text-emerald-700',
      appealed: 'bg-purple-100 text-purple-700',
      voided: 'bg-slate-100 text-slate-700',
      new: 'bg-red-100 text-red-700',
      under_review: 'bg-yellow-100 text-yellow-700',
      overturned: 'bg-green-100 text-green-700',
      upheld: 'bg-slate-100 text-slate-700',
      written_off: 'bg-slate-100 text-slate-500'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status.replace('_', ' ')}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="billing-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-slate-600">Loading billing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="billing-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="billing-title">
              <DollarSign className="w-7 h-7 text-teal-600" />
              Billing & Revenue Cycle
            </h1>
            <p className="text-slate-600 mt-1">Claims management, payments, and revenue analytics</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Billed MTD</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="stat-billed">
                    {formatCurrency(dashboard.summary?.total_billed_mtd)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">{claimsSummary.total_claims || 0} claims</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Collected MTD</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="stat-collected">
                    {formatCurrency(dashboard.summary?.total_collected_mtd)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-2">
                <ArrowUpRight className="w-3 h-3 text-green-500" />
                <span className="text-xs text-green-600">{dashboard.summary?.collection_rate}% collection rate</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Denied MTD</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="stat-denied">
                    {formatCurrency(dashboard.summary?.total_denied_mtd)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">{dashboard.summary?.open_denials} open denials</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Claims</p>
                  <p className="text-2xl font-bold text-yellow-600" data-testid="stat-pending">
                    {dashboard.summary?.pending_claims}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">Ready for submission</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="claims" data-testid="tab-claims">
            <FileText className="w-4 h-4 mr-2" />
            Claims
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            <CreditCard className="w-4 h-4 mr-2" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="denials" data-testid="tab-denials">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Denials
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <TrendingUp className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Claims by Status */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg">Claims by Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard && Object.entries(dashboard.claims_by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(status)}
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Payments */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg">Recent Payments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.recent_payments?.slice(0, 5).map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div>
                        <p className="font-medium text-sm">{payment.patient_name}</p>
                        <p className="text-xs text-slate-500">{payment.payer_name}</p>
                      </div>
                      <span className="font-semibold text-green-600">{formatCurrency(payment.amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Urgent Denials */}
          {dashboard?.urgent_denials?.length > 0 && (
            <Card className="bg-red-50 border-red-200">
              <CardHeader>
                <CardTitle className="text-lg text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Urgent Denials Requiring Attention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.urgent_denials.map((denial) => (
                    <div key={denial.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div>
                        <p className="font-medium">{denial.patient_name} - {denial.claim_number}</p>
                        <p className="text-sm text-slate-500">{denial.denial_reason}</p>
                        <p className="text-xs text-red-600">Appeal deadline: {denial.appeal_deadline}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-600">{formatCurrency(denial.billed_amount)}</p>
                        <Button size="sm" variant="outline" className="mt-1" onClick={() => handleViewDenial(denial.id)}>
                          Review
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Claims Tab */}
        <TabsContent value="claims" className="space-y-4">
          <Card className="bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Claims Management</CardTitle>
                  <CardDescription>View and manage billing claims</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select value={claimStatusFilter || "all"} onValueChange={(val) => setClaimStatusFilter(val === "all" ? "" : val)}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="denied">Denied</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Claim #</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Patient</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Payer</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Service Date</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Billed</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {claims.slice(0, 15).map((claim) => (
                    <tr key={claim.id} className="border-b hover:bg-slate-50" data-testid={`claim-${claim.id}`}>
                      <td className="p-3 text-sm font-mono">{claim.claim_number}</td>
                      <td className="p-3 text-sm">{claim.patient_name}</td>
                      <td className="p-3 text-sm">{claim.payer_name}</td>
                      <td className="p-3 text-sm">{claim.service_date}</td>
                      <td className="p-3 text-sm font-medium">{formatCurrency(claim.billed_amount)}</td>
                      <td className="p-3">{getStatusBadge(claim.status)}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewClaim(claim.id)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {claim.status === 'pending' && (
                            <Button variant="ghost" size="sm" onClick={() => handleSubmitClaim(claim.id)} data-testid={`submit-${claim.id}`}>
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Payment History</CardTitle>
              <CardDescription>
                Total: {formatCurrency(paymentsSummary.total_amount)} from {paymentsSummary.total_payments} payments
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Date</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Claim #</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Patient</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Payer</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Method</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Amount</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.slice(0, 15).map((payment) => (
                    <tr key={payment.id} className="border-b hover:bg-slate-50" data-testid={`payment-${payment.id}`}>
                      <td className="p-3 text-sm">{payment.payment_date}</td>
                      <td className="p-3 text-sm font-mono">{payment.claim_number}</td>
                      <td className="p-3 text-sm">{payment.patient_name}</td>
                      <td className="p-3 text-sm">{payment.payer_name}</td>
                      <td className="p-3">
                        <Badge variant="outline">{payment.payment_method.toUpperCase()}</Badge>
                      </td>
                      <td className="p-3 text-sm font-medium text-green-600">{formatCurrency(payment.amount)}</td>
                      <td className="p-3">{getStatusBadge(payment.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Denials Tab */}
        <TabsContent value="denials" className="space-y-4">
          <div className="grid grid-cols-4 gap-4 mb-4">
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{denialsSummary.total_denials || 0}</p>
                <p className="text-sm text-slate-500">Total Denials</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-800">{formatCurrency(denialsSummary.total_denied_amount)}</p>
                <p className="text-sm text-slate-500">Total Amount</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{denialsSummary.overturn_rate || 0}%</p>
                <p className="text-sm text-slate-500">Overturn Rate</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{denialsSummary.by_status?.new || 0}</p>
                <p className="text-sm text-slate-500">New (Action Needed)</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Denial Management</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Claim #</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Patient</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Payer</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Denial Code</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Amount</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Deadline</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {denials.map((denial) => (
                    <tr key={denial.id} className="border-b hover:bg-slate-50" data-testid={`denial-${denial.id}`}>
                      <td className="p-3 text-sm font-mono">{denial.claim_number}</td>
                      <td className="p-3 text-sm">{denial.patient_name}</td>
                      <td className="p-3 text-sm">{denial.payer_name}</td>
                      <td className="p-3">
                        <span className="text-sm font-mono text-red-600">{denial.denial_code}</span>
                      </td>
                      <td className="p-3 text-sm font-medium">{formatCurrency(denial.billed_amount)}</td>
                      <td className="p-3 text-sm text-red-600">{denial.appeal_deadline}</td>
                      <td className="p-3">{getStatusBadge(denial.status)}</td>
                      <td className="p-3">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDenial(denial.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Revenue Summary */}
            {revenueAnalytics && (
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Revenue Summary (Month)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600">Total Billed</p>
                        <p className="text-xl font-bold text-blue-800">{formatCurrency(revenueAnalytics.summary?.total_billed)}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600">Total Paid</p>
                        <p className="text-xl font-bold text-green-800">{formatCurrency(revenueAnalytics.summary?.total_paid)}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">Collection Rate</span>
                      <span className="font-bold text-lg">{revenueAnalytics.summary?.collection_rate}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* A/R Aging */}
            {agingReport && (
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">A/R Aging Report</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(agingReport.aging_buckets || {}).map(([bucket, data]) => (
                      <div key={bucket} className="flex items-center justify-between p-2 border-b">
                        <span className="text-sm font-medium">{bucket} days</span>
                        <div className="text-right">
                          <span className="font-semibold">{formatCurrency(data.amount)}</span>
                          <span className="text-xs text-slate-400 ml-2">({data.count} claims)</span>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-2 bg-slate-100 rounded mt-2">
                      <span className="font-semibold">Total Outstanding</span>
                      <span className="font-bold text-lg">{formatCurrency(agingReport.total_outstanding)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Denial Trends */}
          {denialTrends && (
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg">Denial Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <h4 className="font-medium text-sm text-slate-500 mb-2">By Category</h4>
                    {denialTrends.by_category?.slice(0, 5).map((item) => (
                      <div key={item.category} className="flex justify-between py-1">
                        <span className="text-sm">{item.category}</span>
                        <span className="font-medium">{item.count}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-slate-500 mb-2">By Payer</h4>
                    {denialTrends.by_payer?.slice(0, 5).map((item) => (
                      <div key={item.payer} className="flex justify-between py-1">
                        <span className="text-sm truncate max-w-[150px]">{item.payer}</span>
                        <span className="font-medium">{item.count}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-slate-500 mb-2">Top Denial Codes</h4>
                    {denialTrends.top_denial_codes?.map((item) => (
                      <div key={item.code} className="flex justify-between py-1">
                        <span className="text-sm font-mono text-red-600">{item.code}</span>
                        <span className="font-medium">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Claim Detail Dialog */}
      <Dialog open={claimDetailOpen} onOpenChange={setClaimDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Claim Details</DialogTitle>
            <DialogDescription>{selectedClaim?.claim?.claim_number}</DialogDescription>
          </DialogHeader>
          {selectedClaim && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Patient</p>
                  <p className="font-medium">{selectedClaim.claim.patient_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Payer</p>
                  <p className="font-medium">{selectedClaim.claim.payer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Service Date</p>
                  <p className="font-medium">{selectedClaim.claim.service_date}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Service Type</p>
                  <p className="font-medium">{selectedClaim.service_type_info?.name || selectedClaim.claim.service_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Billed Amount</p>
                  <p className="font-medium text-lg">{formatCurrency(selectedClaim.claim.billed_amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  {getStatusBadge(selectedClaim.claim.status)}
                </div>
              </div>
              
              {selectedClaim.claim.status === 'denied' && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700">Denial Reason</p>
                  <p className="text-sm text-red-600">{selectedClaim.claim.denial_code}: {selectedClaim.claim.denial_reason}</p>
                </div>
              )}
              
              {selectedClaim.payments?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Payments</p>
                  {selectedClaim.payments.map((p) => (
                    <div key={p.id} className="flex justify-between p-2 bg-green-50 rounded">
                      <span>{p.payment_date}</span>
                      <span className="font-medium text-green-600">{formatCurrency(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimDetailOpen(false)}>Close</Button>
            {selectedClaim?.claim?.status === 'pending' && (
              <Button onClick={() => { handleSubmitClaim(selectedClaim.claim.id); setClaimDetailOpen(false); }}>
                <Send className="w-4 h-4 mr-2" />
                Submit Claim
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Denial Detail Dialog */}
      <Dialog open={denialDetailOpen} onOpenChange={setDenialDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Denial Details</DialogTitle>
            <DialogDescription>{selectedDenial?.denial?.claim_number}</DialogDescription>
          </DialogHeader>
          {selectedDenial && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Patient</p>
                  <p className="font-medium">{selectedDenial.denial.patient_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Payer</p>
                  <p className="font-medium">{selectedDenial.denial.payer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Billed Amount</p>
                  <p className="font-medium">{formatCurrency(selectedDenial.denial.billed_amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Appeal Deadline</p>
                  <p className="font-medium text-red-600">{selectedDenial.denial.appeal_deadline}</p>
                </div>
              </div>
              
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-sm font-medium text-red-700">Denial Code: {selectedDenial.denial.denial_code}</p>
                <p className="text-sm text-red-600 mt-1">{selectedDenial.denial.denial_reason}</p>
              </div>
              
              {selectedDenial.appeal_templates?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Appeal Templates</p>
                  <div className="space-y-1">
                    {selectedDenial.appeal_templates.map((tpl) => (
                      <div key={tpl.id} className="flex items-center justify-between p-2 border rounded">
                        <span className="text-sm">{tpl.name}</span>
                        <Button variant="ghost" size="sm">Use</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDenialDetailOpen(false)}>Close</Button>
            {selectedDenial?.denial?.status === 'new' && (
              <Button onClick={() => handleAppealDenial(selectedDenial.denial.id)} data-testid="appeal-btn">
                <FileText className="w-4 h-4 mr-2" />
                File Appeal
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
